import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
// @ts-ignore
import { logoBase64 } from "constants";

/**
 * Helper function to capitalize a string
 * Converts first letter of each word to uppercase
 * Works with camelCase, snake_case, and space-separated words
 */
const capitalize = (str: string): string => {
  if (typeof str !== "string") return str;

  return str
    .split(/(?=[A-Z])|\s+|_+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Helper function to capitalize string values in data
 * Leaves non-string values unchanged
 */
const capitalizeData = (data: any[]): any[] => {
  return data.map((row) => {
    const newRow = { ...row };
    for (const key in newRow) {
      if (typeof newRow[key] === "string") {
        newRow[key] = capitalize(newRow[key]);
      }
    }
    return newRow;
  });
};

/**
 * Exports data to CSV format with capitalized headers and string values
 */
export const handleCsvExport = (data: any[], fileName: string) => {
  if (!data || !data.length) return;

  // Get headers and capitalize them
  const rawHeaders = Object.keys(data[0]);
  const capitalizedHeaders = rawHeaders.map((header) => capitalize(header));

  // Capitalize string data values
  const capitalizedData = capitalizeData(data);

  const csvRows: string[] = [];

  // === Custom Header Section ===
  const companyName = "NC Group";
  const reportTitle = "Deposit Report";
  const currentDate = new Date().toLocaleDateString();

  csvRows.push(`"${companyName}"`);
  csvRows.push(`"${reportTitle}"`);
  csvRows.push(`"Date: ${currentDate}"`);
  csvRows.push("");

  // === CSV Table Header (Capitalized) ===
  csvRows.push(capitalizedHeaders.join(","));

  // === CSV Data Rows (with capitalized string values) ===
  for (const row of capitalizedData) {
    const values = rawHeaders.map((header) => {
      const escaped = ("" + row[header]).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  }

  // === Generate CSV File ===
  const csvContent = csvRows.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const timestamp = new Date().toISOString().split("T")[0];
  link.href = url;
  link.setAttribute("download", `${fileName}_${timestamp}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exports data to PDF format with capitalized headers and string values
 */
export const handlePdfExport = (
  data: any[],
  fileName: string,
  orientation: "portrait" | "landscape" = "portrait",
  excludeColumns: string[] = [],
) => {
  if (!data || data.length === 0) return;

  const doc = new jsPDF({ orientation });

  // Get headers, filter excluded columns, and capitalize
  const allHeaders = Object.keys(data[0]);
  const includedHeaders = allHeaders.filter(
    (key) => !excludeColumns.includes(key),
  );
  const capitalizedHeaders = includedHeaders.map((header) =>
    capitalize(header),
  );

  // Capitalize string data values
  const capitalizedData = capitalizeData(data);

  // Prepare rows for the table
  const rows = capitalizedData.map((item) =>
    includedHeaders.map((header) => item[header]),
  );

  const pageWidth = doc.internal.pageSize.getWidth();

  // ==== Custom Header ====
  const companyName = "NC Group";
  const reportTitle = `${fileName} Report`;
  const currentDate = new Date().toLocaleDateString();

  doc.setFontSize(16);
  doc.setTextColor(40);
  doc.setFont("helvetica", "bold");
  doc.text(companyName, pageWidth / 2, 15, { align: "center" });

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(reportTitle, pageWidth / 2, 22, { align: "center" });

  doc.setFontSize(10);
  doc.text(`Date: ${currentDate}`, pageWidth - 20, 10, { align: "right" });

  // ==== Optional Logo ====
  doc.addImage(logoBase64, "jpg", 10, 8, 20, 20);

  // ==== Table with Capitalized Headers and Data ====
  autoTable(doc, {
    startY: 30,
    head: [capitalizedHeaders],
    body: rows,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      overflow: "linebreak",
      cellWidth: "wrap",
    },
    headStyles: {
      fillColor: "#1364a6",
      textColor: 255,
    },
  });

  const timestamp = new Date().toISOString().split("T")[0];
  doc.save(`${fileName}_${timestamp}.pdf`);
};

/**
 * Exports data to Excel format with capitalized headers and string values
 */
export const handleExcelExport = (
  data: any[],
  fileName: string,
  excludeColumns: string[] = [],
) => {
  if (!data || data.length === 0) return;

  // Get headers, filter excluded columns, and capitalize
  const headers = Object.keys(data[0]).filter(
    (key) => !excludeColumns.includes(key),
  );
  const capitalizedHeaders = headers.map((header) => capitalize(header));

  // Capitalize string data values
  const capitalizedData = capitalizeData(data);

  // Filter data to exclude unwanted columns
  const filteredData = capitalizedData.map((item) =>
    Object.fromEntries(headers.map((header) => [header, item[header]])),
  );

  // Custom header information
  const companyName = "NC Group";
  const reportTitle = "Deposit Report";
  const currentDate = new Date().toLocaleDateString();

  // Convert data to sheet
  const sheetData: any[][] = [];

  // === Custom Header Rows ===
  sheetData.push([companyName]);
  sheetData.push([reportTitle]);
  sheetData.push([`Date: ${currentDate}`]);
  sheetData.push([]);

  // === Table headers (Capitalized) ===
  sheetData.push(capitalizedHeaders);

  // === Table data (with capitalized string values) ===
  filteredData.forEach((row) => {
    sheetData.push(headers.map((key) => row[key]));
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

  // Export file
  const timestamp = new Date().toISOString().split("T")[0];
  XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
};

/**
 * Calculates total loan amount based on loan data and repayment frequency
 */
export const calculateLoanTotal = (loanData: any, repaymentEvery = 1) => {
  // Extract values from loanData object
  const loanAmount = parseFloat(loanData.loanAmount);
  const interestRate = parseFloat(loanData.interestRate);
  const loanTerm = parseFloat(loanData.loanTerm);
  // @ts-ignore
  repaymentEvery = parseFloat(repaymentEvery);

  const totalRepayments = loanTerm / repaymentEvery; // Total number of repayments
  const ratePerPeriod = (interestRate / 100) * repaymentEvery; // Interest rate per repayment period

  // EMI Calculation
  const emi =
    (loanAmount *
      ratePerPeriod *
      Math.pow(1 + ratePerPeriod, totalRepayments)) /
    (Math.pow(1 + ratePerPeriod, totalRepayments) - 1);

  // Calculate total payments
  const totalPayments = emi * totalRepayments;

  return Math.round(totalPayments);
};

/**
 * Generates a secure password matching the pattern:
 * /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/
 *
 * The password will contain at least:
 * - One lowercase letter
 * - One uppercase letter
 * - One digit
 * - One special character from the set @$!%*?&
 *
 * @param {number} length - The length of the password (minimum 8 recommended)
 * @returns {string} - A randomly generated password matching the requirements
 */
export function generateSecurePassword(length = 8) {
  // Ensure minimum length
  if (length < 4) {
    length = 8; // Set a reasonable minimum if too short
    console.warn("Password length too short, defaulting to 8 characters");
  }

  // Define character sets
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const specialChars = "@$!%*?&";

  // All allowed characters for the password
  const allChars = lowercase + uppercase + digits + specialChars;

  // Function to get a random character from a string
  const getRandomChar = (charSet: any) => {
    return charSet.charAt(Math.floor(Math.random() * charSet.length));
  };

  // Start with one character from each required set
  let password = [
    getRandomChar(lowercase),
    getRandomChar(uppercase),
    getRandomChar(digits),
    getRandomChar(specialChars),
  ];

  // Fill the rest with random characters from the allowed set
  for (let i = 4; i < length; i++) {
    password.push(getRandomChar(allChars));
  }

  // Shuffle the password to avoid predictable patterns
  // (Fisher-Yates shuffle algorithm)
  for (let i = password.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join("");
}

/**
 * Parses user data from an uploaded Excel file
 */
export const parseUserFile = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        if (!e.target || !(e.target.result instanceof ArrayBuffer)) {
          reject(new Error("File could not be read as an ArrayBuffer."));
          return;
        }
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Take the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        // Map to your required format
        const users = rawData.map((row: any) => {
          const r = row as Record<string, any>;
          return {
            username: r["First Name"]?.trim() + " " + r["Last Name"]?.trim(),
            isFirstTimeLogin: "true",
            resetToken: Array.from(
              window.crypto.getRandomValues(new Uint8Array(32)),
            )
              .map((b) => b.toString(16).padStart(2, "0"))
              .join(""),

            password: generateSecurePassword(),
            email: r["Email"]?.trim() || "",
            phone: r["Phone Number"]
              ? r["Phone Number"].toString().trim().startsWith("+")
                ? r["Phone Number"].toString().trim()
                : `+${r["Phone Number"].toString().trim()}`
              : "",

            serviceProvider:
              window.location.hostname
                .replace(/-/g, " ")
                .replace(/.nevasacomptech.com/g, " ") ?? "",
          };
        });

        resolve(users);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};

export const formatCurrency = (value: any) => {
  if (value == null) return "0";

  const absValue = Math.abs(Number(value));
  const formatter = new Intl.NumberFormat("en-US");

  if (absValue >= 1_000_000_000_000) {
    return (
      formatter.format(Number((value / 1_000_000_000_000).toFixed(2))) + "T"
    );
  } else if (absValue >= 1_000_000_000) {
    return formatter.format(Number((value / 1_000_000_000).toFixed(2))) + "B";
  } else if (absValue >= 1_000_000) {
    return formatter.format(Number((value / 1_000_000).toFixed(2))) + "M";
  }

  return formatter.format(value);
};

export function maskEmail(email: any) {
  if (!email) return "";

  const [local, domain] = email.split("@");
  if (!domain) return email;

  const maskedLocal =
    local.length > 2
      ? local.slice(0, 2) + "*".repeat(local.length - 2)
      : local[0] + "*";

  return `${maskedLocal}@${domain}`;
}
